import { DemandForm } from '@features/DemandFormTemplates/model/DemandForm.ts';
import { Dispatch, SetStateAction, createContext, useContext } from 'react';
import { Version } from '@api/document/model/types.ts';
import { Option } from '@components/MultiSelect/MultiSelect.tsx';

export interface Step {
  text: string;
  description: string;
  tooltipQuestionTitle: string;
  advancedKey?: keyof DemandForm;
}

export enum FormStateType {
  EDITING = 'editing',
  CONFIRMATION = 'confirmation',
}

export interface CloudFiles {
  name: string;
}

export interface CustomIDemandDocumentParser {
  id?: number;
  name?: string;
  ai_prompt?: string;
  sections?: Option[];
  files?: File[] | CloudFiles[];
  is_edit: boolean;
}
export interface DirtyFormDataSections {
  introduction: {
    [key: string]: any;
  };
  biography: {
    [key: string]: any;
  };
  fact: {
    [key: string]: any;
  };
  liability: {
    [key: string]: any;
  };
  medical_treatment: {
    [key: string]: any;
  };
  damage: {
    [key: string]: any;
  };
  non_economic_damage: {
    [key: string]: any;
  };
  icd: {
    [key: string]: any;
  };
}

export interface CaseInformationFiles {
  ai_prompt: string;
  parser_id?: number;
  sections: {
    [key: string]: any;
  }[];
  files: File[] | CloudFiles[];
  should_be_parsed: boolean;
}

export interface DirtyFormData {
  document_id?: number;
  sections: DirtyFormDataSections;
  case_information_files_to_be_parsed: CaseInformationFiles[];
  should_be_parsed: boolean;
}

export type DemandFormMode = 'edit' | 'create' | null;
export interface DemandFormContextValues {
  showFormChangedModal: boolean;
  setFormShowChangedModal: Dispatch<SetStateAction<boolean>>;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  currentStep: number;
  formState: FormStateType;
  setFormState: Dispatch<SetStateAction<FormStateType>>;
  steps: Step[];
  mode: DemandFormMode;
  setMode: Dispatch<SetStateAction<DemandFormMode>>;
  setSteps: Dispatch<SetStateAction<Step[]>>;
  versionData: any | null;
  setVersionData?: (data: Version) => void;
  setVersionDataVariables: (data: any | null) => void;
  setModeVariables: (mode: DemandFormMode) => void;
  setIsShowForm?: Dispatch<SetStateAction<boolean>>;
  isShowForm?: boolean;
  customDocumentParsers?: CustomIDemandDocumentParser[];
  setCustomDocumentParsers?: Dispatch<
    SetStateAction<CustomIDemandDocumentParser[]>
  >;
  dirtyFormData: DirtyFormData;
  setDirtyFormData: Dispatch<SetStateAction<DirtyFormData>>;
}

const DemandMonoFormContext = createContext<DemandFormContextValues>({
  showFormChangedModal: false,
  setFormShowChangedModal: () => {},
  currentStep: 0,
  formState: FormStateType.EDITING,
  setFormState: () => {},
  setCurrentStep: () => {},
  steps: [],
  setSteps: () => {},
  mode: null,
  setMode: () => {},
  versionData: null,
  setVersionDataVariables: () => {},
  setModeVariables: () => {},
  setIsShowForm: () => {},
  isShowForm: false,
  customDocumentParsers: [],
  setCustomDocumentParsers: () => {},
  dirtyFormData: {
    sections: {
      introduction: {},
      biography: {},
      liability: {},
      medical_treatment: {},
      fact: {},
      damage: {},
      non_economic_damage: {},
      icd: {},
    },
    case_information_files_to_be_parsed: [],
    should_be_parsed: false,
  },
  setDirtyFormData: () => {},
});

export const useMonoDemandFormContext = () => useContext(DemandMonoFormContext);

export { DemandMonoFormContext };
